import React, { useEffect, useRef, useState } from 'react';
import CustomIcon from '@/components/common/CustomIcon';

const CARD_CONTENTS = [
  {
    title: "Capture what matters",
    description: "After a conversation, log the tiny detail — a favorite snack, a big deadline, something they’re excited about. \n \nWabisabi holds it for you.",
    isDark: true,
    icon: "arrow-right1"
  },
  {
    title: "Let Wabisabi do the remembering",
    description: "We gently remind you when it counts — before a birthday, during a tough week, or right when a thoughtful gesture would mean the most.",
    isDark: false,
    icon: "arrow-right1"
  },
  {
    title: "Show up thoughtfully",
    description: "With a quick nudge, you’ll recall exactly what they said so you can send that message, grab their favorite treat, or simply say, I’m thinking of you.",
    isDark: false,
    icon: "arrow-right1"
  }
];

const HowItWorksSection = () => {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === titleRef.current) {
              // Title animation handled separately if needed
            } else {
              const index = cardRefs.current.indexOf(entry.target as HTMLDivElement);
              if (index !== -1 && !visibleCards.includes(index)) {
                setTimeout(() => {
                  setVisibleCards(prev => [...prev, index]);
                }, index * 200); // Stagger the animations
              }
            }
          }
        });
      },
      { threshold: 0.2 }
    );

    if (titleRef.current) observer.observe(titleRef.current);
    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [visibleCards]);

  return (
    <section ref={sectionRef} className="section-padding bg-[#F4978E] mx-[20px] md:mx-0 rounded-[8px] md:rounded-none">
      <div className="relative z-10 max-w-7xl mx-auto">
        <div
          ref={titleRef}
          className="pl-0 md:pl-[100px] section-container mx-[20px] md:mx-[80px]"
        >
          <h2
            className="mb-6 text-[#2C2C2C] md:max-w-[700px] text-center md:text-left font-bold leading-none"
            style={{
              fontSize: '28px',
              letterSpacing: '-1px',
              verticalAlign: 'middle'
            }}
          >
            <style dangerouslySetInnerHTML={{
              __html: `
                @media (min-width: 768px) {
                  .responsive-title {
                    font-size: 60px !important;
                    letter-spacing: -2px !important;
                  }
                }
              `
            }} />
            <span className="responsive-title">We help you remember the things that show you care.</span>
          </h2>
          <p className="mb-12 text-[#2C2C2C]/80 max-w-3xl md:max-w-[700px] text-center md:text-left" style={{ fontSize: '14px', fontFamily: 'Euclid Circular A' }}>
            <style dangerouslySetInnerHTML={{
              __html: `
                @media (min-width: 768px) {
                  .section-container p {
                    font-size: 1.25rem !important;
                  }
                }
              `
            }} />
            It's not just about birthdays. It's about knowing their coffee order, checking in when they're overwhelmed, or remembering what made them smile.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-8 md:gap-5 px-[20px] md:px-0 justify-center mx-auto md:items-stretch" style={{ overflow: 'visible' }}>
          <style dangerouslySetInnerHTML={{
            __html: `
              @media (min-width: 768px) {
                .card-responsive {
                  width: 416px !important;
                }
              }
            `
          }} />
          {CARD_CONTENTS.map((card, index) => (
            <div
              key={index}
              ref={(el) => cardRefs.current[index] = el}
              className={`flex flex-col items-start text-left ${card.isDark ? 'bg-[#141414] text-white' : 'bg-white'} p-4 py-6 md:py-[50px] md:p-10 shadow-soft hover:shadow-lg border border-black group transform transition-all duration-700 w-full md:w-auto card-responsive ${
                visibleCards.includes(index)
                  ? 'opacity-100 translate-y-0 scale-100'
                  : 'opacity-0 translate-y-8 scale-95'
              } hover:-translate-y-1`}
              style={{ maxWidth: '100%', borderRadius: '8px', gap: '16px', overflow: 'visible' }}
            >
              <div className="flex-1">
                <h3
                  className={`mb-4 py-2 ${card.isDark ? 'text-white' : 'text-black'} transition-colors duration-300`}
                  style={{
                    fontFamily: 'Euclid Circular A',
                    fontWeight: 700,
                    fontSize: '16px',
                    lineHeight: '100%',
                    letterSpacing: '0px',
                    verticalAlign: 'middle'
                  }}
                >
                  {card.title}
                </h3>
                <p
                  className={`${card.isDark ? 'text-white group-hover:text-gray-200' : 'text-[#2C2C2C] group-hover:text-[#1A1A1A]'} transition-colors duration-300`}
                  style={{
                    fontFamily: 'Euclid Circular A',
                    fontWeight: 400,
                    fontSize: '14px',
                    letterSpacing: '0px',
                    whiteSpace: 'pre-line'
                  }}
                >
                  {card.description}
                </p>
              </div>
              <CustomIcon
                name={card.icon}
                size={34}
                color={card.isDark ? 'white' : 'black'}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
